<template>
  <div>
    <b-tabs v-model="activeTab" content-class="mt-3">

      <!-- ── Tab 1: Submissions ─────────────────────────────────────────── -->
      <b-tab title="My Submissions">
        <b-card no-body>
          <div class="m-2 d-flex flex-wrap gap-2 align-items-center justify-content-between">
            <div class="d-flex flex-wrap gap-2">
              <b-form-select
                v-model="filterFarmId"
                :options="farmOptions"
                size="sm"
                style="width:160px;"
                @change="onFarmChange"
              />
              <b-form-select
                v-model="filterShedId"
                :options="shedOptions"
                size="sm"
                style="width:140px;"
                @change="loadSubmissions"
              />
              <b-form-select
                v-model="filterStage"
                :options="stageOptions"
                size="sm"
                style="width:130px;"
                @change="loadSubmissions"
              />
              <b-form-select
                v-model="filterLevel"
                :options="levelOptions"
                size="sm"
                style="width:110px;"
                @change="loadSubmissions"
              />
            </div>
            <span class="text-muted small">{{ totalSubmissions }} total</span>
          </div>

          <b-table
            responsive
            small
            :items="submissions"
            :fields="submissionFields"
            :busy="submissionsLoading"
            show-empty
            empty-text="No submissions yet."
          >
            <template #table-busy>
              <div class="text-center my-3"><b-spinner variant="primary" /></div>
            </template>

            <template #cell(image_url)="{ item }">
              <a v-if="item.image_url" :href="item.image_url" target="_blank">
                <img :src="item.image_url" style="height:44px;width:44px;object-fit:cover;border-radius:4px;" alt="slide" />
              </a>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(level)="{ item }">
              <b-badge v-if="item.level" :style="levelBadgeStyle(item.level)">
                Level {{ item.level }} — {{ levelLabel(item.level) }}
              </b-badge>
              <span v-else class="text-muted">Pending</span>
            </template>

            <template #cell(classification_method)="{ item }">
              <b-badge :variant="methodBadgeVariant(item.classification_method)">
                {{ methodBadgeLabel(item.classification_method) }}
              </b-badge>
            </template>

            <template #cell(recommendation)="{ item }">
              <span class="small text-muted" style="max-width:200px;display:block;">{{ item.recommendation || '—' }}</span>
            </template>
          </b-table>

          <div class="d-flex justify-content-center my-2" v-if="totalSubmissions > 30">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalSubmissions"
              :per-page="30"
              size="sm"
              @change="loadSubmissions"
            />
          </div>
        </b-card>
      </b-tab>

      <!-- ── Tab 2: Analytics ───────────────────────────────────────────── -->
      <b-tab title="Analytics">
        <b-card no-body>
          <div class="m-2 d-flex align-items-center gap-2">
            <b-form-select v-model="analyticsPeriod" :options="periodOptions" size="sm" style="width:130px;" @change="loadAnalytics" />
          </div>

          <b-overlay :show="analyticsLoading" rounded="sm">
            <div class="p-3" v-if="analytics">

              <!-- KPIs -->
              <b-row class="mb-3">
                <b-col cols="6" md="3" class="mb-2">
                  <b-card class="text-center" bg-variant="light">
                    <h3 class="mb-0">{{ analytics.total }}</h3>
                    <small class="text-muted">Total Tests</small>
                  </b-card>
                </b-col>
                <b-col cols="6" md="3" class="mb-2">
                  <b-card class="text-center" bg-variant="light">
                    <h3 class="mb-0">{{ analytics.stage_dist.post || 0 }}</h3>
                    <small class="text-muted">Post-Clean Tests</small>
                  </b-card>
                </b-col>
                <b-col cols="6" md="3" class="mb-2">
                  <b-card class="text-center" bg-variant="light">
                    <h3 class="mb-0">{{ analytics.stage_dist.pre || 0 }}</h3>
                    <small class="text-muted">Pre-Clean Tests</small>
                  </b-card>
                </b-col>
                <b-col cols="6" md="3" class="mb-2">
                  <b-card class="text-center" bg-variant="light">
                    <h3 class="mb-0">{{ analytics.method_dist.gemini || 0 }}</h3>
                    <small class="text-muted">AI Classified</small>
                  </b-card>
                </b-col>
              </b-row>

              <!-- Level distribution -->
              <h6 class="mb-2">Contamination Level Distribution</h6>
              <div class="d-flex gap-2 align-items-flex-end mb-4" style="height:80px;">
                <div v-for="n in [1,2,3,4,5]" :key="n" class="text-center" style="flex:1;">
                  <div :style="{height: levelBarHeight(n) + 'px', backgroundColor: levelColors[n], borderRadius: '4px 4px 0 0'}" />
                  <small>L{{ n }}: {{ analytics.level_dist[n] || 0 }}</small>
                </div>
              </div>

              <!-- Shed-wise report -->
              <h6 class="mb-2">Shed-wise Report</h6>
              <b-table
                :items="analytics.shed_dist"
                :fields="shedDistFields"
                small
                responsive
                show-empty
                empty-text="No shed data."
                class="mb-0"
              >
                <template #cell(avg_level)="{ item }">
                  <b-badge :style="levelBadgeStyle(Math.round(item.avg_level))">
                    {{ item.avg_level }}
                  </b-badge>
                </template>
              </b-table>

            </div>
            <div v-else class="p-4 text-center text-muted">No analytics data yet.</div>
          </b-overlay>
        </b-card>
      </b-tab>

    </b-tabs>
  </div>
</template>

<script>
import axios from '@axios'

const LEVEL_COLORS = {
  1: '#22c55e', 2: '#84cc16', 3: '#eab308', 4: '#f97316', 5: '#ef4444',
}
const LEVEL_LABELS = {
  1: 'Very Low', 2: 'Low', 3: 'Moderate', 4: 'High', 5: 'Very High',
}

export default {
  name: 'HygicultOwner',

  data() {
    return {
      activeTab: 0,

      farms: [],

      submissions:        [],
      submissionsLoading: false,
      totalSubmissions:   0,
      currentPage:        1,
      filterFarmId:       '',
      filterShedId:       '',
      filterStage:        '',
      filterLevel:        '',

      stageOptions: [
        { value: '', text: 'All stages' },
        { value: 'pre', text: 'Pre-clean' },
        { value: 'post', text: 'Post-clean' },
      ],
      levelOptions: [
        { value: '', text: 'All levels' },
        ...[1,2,3,4,5].map(n => ({ value: n, text: `Level ${n}` })),
      ],
      submissionFields: [
        { key: 'image_url',             label: 'Slide' },
        { key: 'farm_name',             label: 'Farm' },
        { key: 'shed',                  label: 'Shed' },
        { key: 'bird_type',             label: 'Type' },
        { key: 'cleaning_stage',        label: 'Stage' },
        { key: 'sample_type',           label: 'Sample' },
        { key: 'level',                 label: 'Result' },
        { key: 'cfu_value',             label: 'CFU' },
        { key: 'recommendation',        label: 'Recommendation' },
        { key: 'classification_method', label: 'Method' },
        { key: 'created_at',            label: 'Date', formatter: v => v ? new Date(v).toLocaleDateString() : '—' },
      ],

      analytics:        null,
      analyticsLoading: false,
      analyticsPeriod:  'month',
      periodOptions: [
        { value: 'week',    text: 'This week' },
        { value: 'month',   text: 'This month' },
        { value: 'quarter', text: 'This quarter' },
        { value: 'year',    text: 'This year' },
      ],
      shedDistFields: [
        { key: 'shed_name', label: 'Shed' },
        { key: 'count',     label: 'Tests', sortable: true },
        { key: 'avg_level', label: 'Avg Level' },
      ],
      levelColors: LEVEL_COLORS,
    }
  },

  computed: {
    farmOptions() {
      return [
        { value: '', text: 'All farms' },
        ...this.farms.map(f => ({ value: f.id, text: f.name })),
      ]
    },
    shedOptions() {
      const farm = this.farms.find(f => f.id === this.filterFarmId)
      return [
        { value: '', text: 'All sheds' },
        ...(farm?.sheds ?? []).map(s => ({ value: s.id, text: s.name })),
      ]
    },
  },

  created() {
    this.loadFarms()
    this.loadSubmissions()
    this.loadAnalytics()
  },

  methods: {
    async loadFarms() {
      try {
        const res = await axios.get('/web/owner/hygicult/farms')
        this.farms = res.data?.farms ?? []
      } catch { /* silent */ }
    },

    onFarmChange() {
      this.filterShedId = ''
      this.currentPage  = 1
      this.loadSubmissions()
    },

    async loadSubmissions() {
      this.submissionsLoading = true
      try {
        const params = { page: this.currentPage }
        if (this.filterFarmId) params.farm_id        = this.filterFarmId
        if (this.filterShedId) params.shed_id        = this.filterShedId
        if (this.filterStage)  params.cleaning_stage = this.filterStage
        if (this.filterLevel)  params.level          = this.filterLevel
        const res = await axios.get('/web/owner/hygicult/submissions', { params })
        this.submissions      = res.data?.data ?? []
        this.totalSubmissions = res.data?.total ?? this.submissions.length
      } catch {
        this.$bvToast.toast('Failed to load submissions', { variant: 'danger', title: 'Error' })
      } finally {
        this.submissionsLoading = false
      }
    },

    async loadAnalytics() {
      this.analyticsLoading = true
      try {
        const res = await axios.get('/web/owner/hygicult/analytics', { params: { period: this.analyticsPeriod } })
        this.analytics = res.data
      } catch {
        this.$bvToast.toast('Failed to load analytics', { variant: 'danger', title: 'Error' })
      } finally {
        this.analyticsLoading = false
      }
    },

    levelBarHeight(n) {
      const count = this.analytics?.level_dist?.[n] ?? 0
      const max   = Math.max(...Object.values(this.analytics?.level_dist ?? { 0: 1 }), 1)
      return Math.round((count / max) * 60) + 4
    },
    levelBadgeStyle(n) {
      return `background-color:${LEVEL_COLORS[n] ?? '#888'};color:#fff;`
    },
    levelLabel(n) { return LEVEL_LABELS[n] ?? '' },
    methodBadgeVariant(method) {
      return { gemini: 'primary', manual: 'warning', coverage: 'secondary' }[method] ?? 'light'
    },
    methodBadgeLabel(method) {
      return { gemini: 'AI', manual: 'Manual', coverage: 'Coverage' }[method] ?? method
    },
  },
}
</script>
